import { useState } from "react";
import { mutate } from "swr";
import { IconPlus } from "@tabler/icons-react";
import { Text } from "../../../../components/common/typography";
import { SectionCard } from "../../../../components/common/section-card";
import { Button } from "../../../../components/common/button";
import { CheckboxField } from "../../../../components/common/checkbox-field/checkbox-field";
import {
  useMyWarrantyProducts,
  MY_WARRANTIES_SWR_KEY,
} from "../../hooks/useWarrantyProducts";
import { WarrantyFormDialog } from "./warranty-form-dialog";

interface Props {
  selectedIds: number[];
  onChange: (next: number[]) => void;
  readOnly?: boolean;
}

/**
 * Lets the seller pick which of their own warranty SKUs the buyer can choose
 * from at checkout. Sellers can also create a new warranty SKU inline via the
 * dialog — saves them a context switch to /my-warranties.
 */
export function CompatibleWarrantiesSection({ selectedIds, onChange, readOnly = false }: Props) {
  const { data, isLoading } = useMyWarrantyProducts();
  const options = data ?? [];
  const selected = new Set(selectedIds);

  const [createOpen, setCreateOpen] = useState(false);

  function toggle(id: number) {
    if (selected.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }

  return (
    <SectionCard
      shadow
      title="Extended warranties"
      subtitle="Pick which of your warranty SKUs the buyer can choose from at checkout. Manage your full catalog at /my-warranties."
      action={
        !readOnly && (
          <Button
            variant="secondaryDark"
            size="sm"
            leftIcon={<IconPlus size={14} />}
            onClick={() => setCreateOpen(true)}
          >
            New warranty
          </Button>
        )
      }
      className="gap-4"
    >
      {isLoading && (
        <Text variant="bodySm" tone="muted">Loading your warranties…</Text>
      )}
      {!isLoading && options.length === 0 && (
        <Text variant="bodySm" tone="muted">
          You haven't created any warranty SKUs yet. Use "New warranty" to add one.
        </Text>
      )}
      {!isLoading && options.length > 0 && (
        <div className="flex flex-col gap-2">
          {options.map((opt) => {
            const isSelected = selected.has(opt.id);
            return (
              <div key={opt.id} className="flex items-center justify-between">
                <CheckboxField
                  checked={isSelected}
                  onChange={() => toggle(opt.id)}
                  disabled={readOnly}
                  label={opt.name}
                  helperText={opt.description ?? undefined}
                  className="flex-1"
                />
                <Text variant="bodySmBold" tone="dark" className="shrink-0 pl-4">
                  {opt.price != null ? `+QAR ${opt.price.toLocaleString()}` : "Included"}
                </Text>
              </div>
            );
          })}
        </div>
      )}

      <WarrantyFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={(saved) => {
          void mutate(MY_WARRANTIES_SWR_KEY);
          // Auto-select the freshly created warranty for this product.
          if (!selected.has(saved.id)) {
            onChange([...selectedIds, saved.id]);
          }
        }}
      />
    </SectionCard>
  );
}
